import { Avatar, Breadcrumb, Button, Dropdown, Image } from "antd";
import { AngleRight, DeleteIcon } from "../CustomIcons";
import { MoreOutlined } from "@ant-design/icons";
import { useRef, useState } from "react";
import ConfirmDelete from "../ConfirmDelete";
import { SuccessMessage } from "../Toasts/SuccessMessage";
import { AxiosError } from "axios";
import { ErrorMessage } from "../Toasts/ErrorMessage";
import {
  changePotentialDealStatus,
  deletePotentialDeal,
  makeDealAsCopied,
} from "../../organisms/PotentialDeals/ducks/services";
import { PopUp } from "../PopUp";
import { getPotentialDealDetails } from "../../organisms/PotentialDeals/ducks/actions";
import { useAppDispatch } from "../../config/hooks";
import { useNavigate } from "react-router-dom";
import { GetDealType } from "../../organisms/PotentialDeals/ducks/types";
// import { di } from "@fullcalendar/core/internal-common";

interface BreadcrumbItem {
  label: string;
  href?: string;
}
interface HeaderProps {
  title: string;
  description?: string | React.ReactNode;
  btnText1?: string;
  btnIcon1?: JSX.Element;
  btnText2?: string;
  breadcrumb?: BreadcrumbItem[];
  imageIcon?: string;
  avatarColor?: string;
  avatarIcon?: React.ReactNode;
  propertyName: string;
  propertyClientName: string;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  detailData: GetDealType | null;
}

export default function PotentialDealsHeader({
  title,
  description,
  btnText1,
  btnIcon1,
  btnText2,
  breadcrumb = [],
  imageIcon,
  avatarIcon,
  avatarColor,
  propertyName,
  propertyClientName,
  loading,
  setLoading,
  detailData,
}: HeaderProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showLostPopUp, setShowLostPopUp] = useState(false);
  const [archivePopUp, setArchivePopUp] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const dealId = pathSegments[pathSegments.length - 2];
  const dropdownRef = useRef<HTMLButtonElement>(null);

  const makeCopyOfDeal = () => {
    setLoading(true);
    makeDealAsCopied(detailData?.id as string)
      .then(() => {
        setLoading(false);
        SuccessMessage("Deal copied successfully");
        navigate(`/potential-deals/${companyId}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-deal/${dealId}/${companyId}`);
          }}
        >
          Edit deal details
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/copy-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            makeCopyOfDeal();
          }}
        >
          Make a copy
        </Button>
      ),
    },
    {
      key: "3",
      disabled: true,
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/save-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
          }}
          disabled={true}
        >
          Save as template
        </Button>
      ),
    },
    {
      key: "4",
      disabled: true,
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/reset-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
          }}
          disabled={true}
        >
          Reset to template
        </Button>
      ),
    },
    {
      key: "5",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/lost-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            setShowLostPopUp(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Mark as lost
        </Button>
      ),
    },
    {
      key: "6",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/archive-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            detailData?.status !== "archived"
              ? setArchivePopUp(true)
              : archiveFunction();
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          {detailData?.status !== "archived" ? "Archive" : "Activate"}
        </Button>
      ),
    },
    {
      key: "7",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const markAsLostFunction = () => {
    setLoading(true);

    const payload = {
      status: "lost",
    };

    if (dealId) {
      changePotentialDealStatus(dealId as string, payload)
        .then(() => {
          setLoading(false);
          dispatch(getPotentialDealDetails(dealId));
          setShowLostPopUp(false);
          SuccessMessage("Deal moved to lost");
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  const markAsLostPopUp = {
    visibility: showLostPopUp,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setShowLostPopUp(false)}
        heading="Confirm the deal is lost"
        paragraph={
          <>
            Only mark this opportunity as lost if it's fully closed.
            <br /> <br />
            If the customer declined the proposal but they are open to
            alternatives, keep it active and create a new proposal for this deal
            instead.
          </>
        }
        isArchivePopup
        btnText="Potential deal is lost"
        btnApi={markAsLostFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setShowLostPopUp(false),
  };

  const archiveFunction = () => {
    setLoading(true);
    const payload = {
      status: detailData?.status !== "archived" ? "archived" : "active",
    };
    changePotentialDealStatus(detailData?.id as string, payload)
      .then(() => {
        setLoading(false);
        setArchivePopUp(false);
        dispatch(getPotentialDealDetails(dealId));
        SuccessMessage(
          `Deal ${
            detailData?.status !== "archived" ? "archived" : "activated"
          }.`
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const confirmArchivedPopUp = {
    visibility: archivePopUp,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setArchivePopUp(false)}
        heading={
          detailData?.status !== "archived"
            ? "Archive potential deal"
            : "Activate potential deal"
        }
        paragraph={
          <>
            Are you sure you want to{" "}
            {detailData?.status !== "archived" ? "archive" : "activate"}{" "}
            <b>{detailData?.name}</b>? <br />
            {detailData?.status !== "archived" && (
              <>
                <br /> You can restore it any moment later.
              </>
            )}
          </>
        }
        btnText={`${
          detailData?.status !== "archived deal"
            ? "Archive deal"
            : "Activate deal"
        } `}
        btnApi={archiveFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setArchivePopUp(false),
  };

  const deleteFunction = () => {
    setLoading(true);
    deletePotentialDeal([detailData?.id || ""])
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        dispatch(getPotentialDealDetails(dealId));
        navigate(`/potential-deals/${companyId}`);
        SuccessMessage("Deal deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const deleteClientPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Delete potential deal"
        paragraph={
          <>
            Are you sure you want to delete <b>{detailData?.name}</b> ?
            <br /> <br />
            It won't be available anymore.
          </>
        }
        btnText="Delete deal"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  return (
    <div className="profileHeader">
      <div className="headerLeft">
        {breadcrumb.length > 0 && (
          <Breadcrumb
            separator={<AngleRight />}
            style={{ marginBottom: "12px" }}
          >
            {breadcrumb.map((item, index) => (
              <Breadcrumb.Item key={index}>
                {item.href ? <a href={item.href}>{item.label}</a> : item.label}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        )}
        {imageIcon || avatarIcon ? (
          <div className="headerContentWithImage">
            {imageIcon && <Image src={imageIcon} preview={false} />}
            {avatarIcon && (
              <Avatar
                size={58}
                className={avatarColor}
                shape="square"
                src={avatarIcon}
              />
            )}
            <div>
              <h2>{title}</h2>
              {description && <p>{description}</p>}
            </div>
          </div>
        ) : (
          <>
            <h2>{title}</h2>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <div
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                <Image
                  src="/images/estimate/building-icon.svg"
                  preview={false}
                />
                <span
                  style={{
                    fontSize: "14px",
                    lineHeight: "20px",
                    fontWeight: "400",
                    color: "#6454F4",
                  }}
                >
                  {" "}
                  {propertyName ? propertyName : "..."}
                </span>
              </div>
              {propertyName && propertyClientName && (
                <span
                  style={{
                    background: "#DEE5E6",
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                  }}
                />
              )}
              <div
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                <Image src="/images/estimate/clients-icon.svg" />{" "}
                <span
                  style={{
                    fontSize: "14px",
                    lineHeight: "20px",
                    fontWeight: "400",
                    color: "#474C4D",
                  }}
                >
                  {propertyClientName}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
      {(btnText1 || btnText2) && (
        <div className="two_btns">
          {btnText1 && (
            <Button htmlType="submit" size="large" className="btn-sm">
              {btnIcon1 && btnIcon1} {btnText1}
            </Button>
          )}
          {btnText2 && (
            <Button htmlType="submit" size="large" className="outlined-btn">
              {btnText2}
            </Button>
          )}
        </div>
      )}
      <div className="headerRight" style={{ marginTop: 10 }}>
        {/* <Button
          htmlType="button"
          type="default"
          size="large"
          className="outlined-btn"
          style={{ marginRight: "8px" }}
        >
          Notes & activity
        </Button> */}

        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(x) => {
              x.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "0px",
              height: "36px",
              width: "36px",
              backgroundColor: "transparent",
              border: "1px solid #DEE5E6",
              borderRadius: "8px",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      </div>
      <PopUp {...markAsLostPopUp} />
      <PopUp {...deleteClientPopUp} />
      <PopUp {...confirmArchivedPopUp} />
    </div>
  );
}
